import { PackApiAdapter } from '../adapters/PackApiAdapter';
import { CreatePackRequest, Pack, PackFilters, PackPage, Product } from '../models/Pack';

export interface UpdatePackRequest {
  title: string;
  items: Array<{
    product_id: number;
    quantity: number;
  }>;
}

export class PackService {
  constructor(private apiAdapter: PackApiAdapter) {}

  async getAllPacks(filters: PackFilters = {}): Promise<Pack[]> {
    return this.apiAdapter.getPacks(filters);
  }

  async getPacksPaginated(filters: PackFilters = {}): Promise<PackPage> {
    const { searchTerm, limit } = filters;

    if (limit && limit > 100) {
      throw new Error('El límite no puede ser mayor a 100');
    }

    if (searchTerm && searchTerm.length < 2) {
      throw new Error('El término de búsqueda debe tener al menos 2 caracteres');
    }

    return this.apiAdapter.getPacksPaginated(filters);
  }

  async getPackById(id: number): Promise<Pack> {
    if (!id || id <= 0) {
      throw new Error('ID de pack inválido');
    }

    return this.apiAdapter.getPackById(id);
  }

  async getProducts(): Promise<Product[]> {
    return this.apiAdapter.getProducts();
  }

  async createPack(data: CreatePackRequest): Promise<Pack> {
    // 🎯 Validaciones de negocio
    if (!data.title || data.title.trim().length < 2) {
      throw new Error('El título del pack debe tener al menos 2 caracteres');
    }

    if (!data.items || data.items.length === 0) {
      throw new Error('El pack debe tener al menos un producto');
    }

    // Validar cada línea del pack
    for (const line of data.items) {
      if (!line.product_id || line.product_id <= 0) {
        throw new Error('Cada línea debe tener un ID de producto válido');
      }

      if (!line.quantity || line.quantity <= 0) {
        throw new Error('La cantidad debe ser mayor a 0');
      }

      if (line.quantity > 1000) {
        throw new Error('La cantidad no puede ser mayor a 1000');
      }
    }

    // Validar que no haya productos duplicados
    const productIds = data.items.map((line) => line.product_id);
    const uniqueProductIds = new Set(productIds);
    if (productIds.length !== uniqueProductIds.size) {
      throw new Error('No puede haber productos duplicados en el pack');
    }

    // 🎯 Aplicar valores por defecto si no se proporcionan
    const packData: CreatePackRequest = {
      title: data.title.trim(),
      ...(data.team_id ? { team_id: data.team_id } : {}),
      items: data.items.map((line) => ({
        product_id: line.product_id,
        quantity: line.quantity,
      })),
    };

    return this.apiAdapter.createPack(packData);
  }

  async updatePack(id: number, data: UpdatePackRequest): Promise<Pack> {
    // 🎯 Validación del ID
    if (!id || id <= 0) {
      throw new Error('ID de pack inválido');
    }

    // 🎯 Validaciones de negocio
    if (!data.title || data.title.trim().length < 2) {
      throw new Error('El título del pack debe tener al menos 2 caracteres');
    }

    if (!data.items || data.items.length === 0) {
      throw new Error('El pack debe tener al menos un producto');
    }

    // Validar cada línea del pack
    for (const line of data.items) {
      if (!line.product_id || line.product_id <= 0) {
        throw new Error('Cada línea debe tener un ID de producto válido');
      }

      if (!line.quantity || line.quantity <= 0) {
        throw new Error('La cantidad debe ser mayor a 0');
      }

      if (line.quantity > 1000) {
        throw new Error('La cantidad no puede ser mayor a 1000');
      }
    }

    // Validar que no haya productos duplicados
    const productIds = data.items.map((line) => line.product_id);
    const uniqueProductIds = new Set(productIds);
    if (productIds.length !== uniqueProductIds.size) {
      throw new Error('No puede haber productos duplicados en el pack');
    }

    // 🎯 Preparar datos para actualización
    const updateData: UpdatePackRequest = {
      title: data.title.trim(),
      items: data.items.map((line) => ({
        product_id: line.product_id,
        quantity: line.quantity,
      })),
    };

    return this.apiAdapter.updatePack(id, updateData);
  }

  async deletePack(id: number): Promise<void> {
    // 🎯 Validación básica
    if (!id || id <= 0) {
      throw new Error('ID de pack inválido');
    }

    return this.apiAdapter.deletePack(id);
  }
}
